import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { MessageSquare, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContractorMessages = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchConversation = useCallback(async () => {
    if (!selectedUser) return;
    try {
      const response = await axios.get(`${API}/messages/conversation/${selectedUser.id}`);
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to load messages");
    }
  }, [selectedUser]);

  useEffect(() => {
    if (selectedUser) {
      fetchConversation();
      const interval = setInterval(fetchConversation, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedUser, fetchConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/chat-users`);
      setUsers(response.data);
      if (response.data.length > 0) {
        setSelectedUser(response.data[0]);
      }
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    try {
      await axios.post(`${API}/messages`, {
        receiver_id: selectedUser.id,
        content: newMessage,
        attachments: []
      });
      setNewMessage("");
      fetchConversation();
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  return (
    <div data-testid="contractor-messages">
      <h1 className="text-4xl font-bold text-[#333333] mb-2">MESSAGES</h1>
      <p className="text-[#52525B] mb-8">Chat with Aggreko admins</p>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Users List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">ADMINS</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {users.map((user) => (
              <div
                key={user.id}
                onClick={() => setSelectedUser(user)}
                className={`p-4 cursor-pointer border-b hover:bg-gray-50 ${
                  selectedUser?.id === user.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                }`}
              >
                <p className="font-medium">{user.name}</p>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>
            ))}
            {users.length === 0 && (
              <p className="p-4 text-gray-400 text-center">No admins available</p>
            )}
          </CardContent>
        </Card>

        {/* Chat Window */}
        <Card className="lg:col-span-3">
          <CardHeader className="border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              {selectedUser ? selectedUser.name : 'Select an admin'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {selectedUser ? (
              <>
                <ScrollArea className="h-[400px] p-4">
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === selectedUser.id ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg px-4 py-2 ${
                            msg.sender_id === selectedUser.id
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-blue-500 text-white'
                          }`}
                        >
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            msg.sender_id === selectedUser.id ? 'text-gray-500' : 'text-blue-200'
                          }`}>
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                <form onSubmit={sendMessage} className="p-4 border-t flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1"
                  />
                  <Button type="submit" className="bg-blue-500 hover:bg-blue-600 text-white">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </>
            ) : (
              <div className="h-[400px] flex items-center justify-center text-gray-400">
                Select an admin to start chatting
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContractorMessages;
